import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Users, Copy, Share2, UserCheck, ShoppingCart, Loader2, Link2, MessageCircle, Gift, Package, Ticket, Star, ChevronDown, ExternalLink, Send } from 'lucide-react';
import { getUserNetwork, getUserPurchases } from '../lib/api';
import api from '../lib/api';
import { useApp } from '../App';
import { toast } from 'sonner';

const PRIZE_MAP_BASICO = { 1: '$10,000', 2: '$5,000', 3: '$2,500', 4: '$1,000', 5: '$500' };
const PRIZE_MAP_DIAMANTE = { 1: '$20,000', 2: '$10,000', 3: '$5,000', 4: '$2,500', 5: '$1,000' };
const PRIZE_MAP_GRATIS = { 1: '$2,000', 2: '$1,500', 3: '$1,000', 4: '$800', 5: '$500' };

const MiRed = () => {
  const { user, selectedRaffle, raffles, setSelectedRaffleId } = useApp();
  const [network, setNetwork] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeLevel, setActiveLevel] = useState('1');
  const [activeTab, setActiveTab] = useState('cofres');
  const [filterRaffleId, setFilterRaffleId] = useState(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramLinked, setTelegramLinked] = useState(false);

  // Cargar datos cuando cambia el usuario o el filtro de sorteo
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Usar filterRaffleId si está seleccionado, sino mostrar global (null)
        const raffleIdToUse = filterRaffleId || null;
        const [netRes, purRes] = await Promise.all([
          getUserNetwork(raffleIdToUse),
          getUserPurchases(raffleIdToUse),
        ]);
        setNetwork(netRes.data);
        setPurchases(purRes.data);
      } catch (err) {
        toast.error('Error cargando datos');
      }
      setLoading(false);
    };
    if (user) fetchData();
  }, [user, filterRaffleId]);

  // Al montar, usar el sorteo seleccionado si existe
  useEffect(() => {
    if (selectedRaffle?.raffle_id && filterRaffleId === null) {
      setFilterRaffleId(selectedRaffle.raffle_id);
    }
  }, [selectedRaffle]);

  const copyCode = () => {
    navigator.clipboard.writeText(user?.referral_code || '');
    toast.success('Código copiado al portapapeles');
  };

  const copyLink = () => {
    const link = `${window.location.origin}/?ref=${user?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Enlace copiado al portapapeles');
  };

  const shareTelegram = () => {
    const link = `${window.location.origin}/?ref=${user?.referral_code}`;
    const text = `🎁 ¡Participa en COFRES MÁGICOS!\n\nYo ya estoy participando por $30,000 pesos.\n\nUsa mi enlace para registrarte y abrir cofres con precios sorpresa:\n\n👉 ${link}\n\nEntre más participes, más oportunidades tienes de ganar.\n\n¡Regístrate y descubre tu suerte! 🍀`;
    window.open(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`, '_blank');
  };

  const connectTelegram = async () => {
    setTelegramLoading(true);
    try {
      const res = await api.get('/user/telegram/link');
      if (res.data.ok && res.data.link) {
        window.open(res.data.link, '_blank');
        toast.success('Abre Telegram y presiona "Iniciar" para vincularte');
      } else {
        toast.error('Telegram no disponible. Inténtalo más tarde.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar enlace de Telegram');
    }
    setTelegramLoading(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={32} className="animate-spin text-[#FFC640]" />
      </div>
    );
  }

  return (
    <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#F7F2E7]">Mi Panel</h1>
        <p className="text-sm text-[#F7F2E7]/50 mt-1">Bienvenido, {user?.name}</p>
      </div>

      {/* Selector de sorteo (solo si hay 2+ sorteos activos) */}
      {raffles && raffles.filter(r => r.status === 'active').length > 1 && (
        <div className="mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-[#F7F2E7]/60">Ver datos de:</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterRaffleId(null)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  filterRaffleId === null
                    ? 'bg-[#FFC640] text-[#090A0D]'
                    : 'bg-[#121521] text-[#F7F2E7]/70 border border-white/[0.1] hover:border-[rgba(255,198,64,0.3)]'
                }`}
              >
                Todos los sorteos
              </button>
              {raffles.filter(r => r.status === 'active').map(raffle => (
                <button
                  key={raffle.raffle_id}
                  onClick={() => setFilterRaffleId(raffle.raffle_id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    filterRaffleId === raffle.raffle_id
                      ? 'bg-[#FFC640] text-[#090A0D]'
                      : 'bg-[#121521] text-[#F7F2E7]/70 border border-white/[0.1] hover:border-[rgba(255,198,64,0.3)]'
                  }`}
                >
                  {raffle.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Referral code card */}
      <Card className="bg-[#121521] border border-[rgba(255,198,64,0.2)] mb-8">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4">
            <div>
              <p className="text-sm text-[#F7F2E7]/60 mb-1">Tu código de referido</p>
              <p className="text-3xl font-heading font-bold text-[#FFC640] tracking-wider" data-testid="mi-red-referral-code">
                {user?.referral_code}
              </p>
              <p className="text-xs text-[#F7F2E7]/40 mt-1">
                Link: {window.location.origin}/?ref={user?.referral_code}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={copyCode} variant="outline"
                className="bg-[#171B2A] border-white/[0.12] text-[#F7F2E7] hover:border-[rgba(255,198,64,0.35)] rounded-xl"
                data-testid="mi-red-copy-code-button">
                <Copy size={16} className="mr-1" /> Copiar código
              </Button>
              <Button onClick={copyLink} variant="outline"
                className="bg-[#171B2A] border-white/[0.12] text-[#F7F2E7] hover:border-[rgba(255,198,64,0.35)] rounded-xl"
                data-testid="mi-red-copy-link-button">
                <Link2 size={16} className="mr-1" /> Copiar enlace
              </Button>
              <Button onClick={shareTelegram}
                className="bg-[#229ED9] text-white hover:bg-[#1a8bc2] rounded-xl"
                data-testid="mi-red-share-telegram-button">
                <Send size={16} className="mr-1" /> Compartir por Telegram
              </Button>
              <Button onClick={connectTelegram} disabled={telegramLoading}
                className="bg-[#229ED9] text-white hover:bg-[#1a8bc2] rounded-xl"
                data-testid="mi-red-connect-telegram-button">
                {telegramLoading ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Send size={16} className="mr-1" />}
                {user?.telegram_chat_id ? 'Telegram vinculado ✓' : 'Conectar Telegram'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main tabs: Mis Cofres / Mi Red */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#121521] border border-white/[0.08] p-1">
          <TabsTrigger value="cofres" className="data-[state=active]:bg-[#FFC640] data-[state=active]:text-[#090A0D] text-[#F7F2E7]/60 gap-1.5">
            <Ticket size={14} /> Mis Cofres
          </TabsTrigger>
          <TabsTrigger value="red" className="data-[state=active]:bg-[#FFC640] data-[state=active]:text-[#090A0D] text-[#F7F2E7]/60 gap-1.5">
            <Users size={14} /> Mi Red
          </TabsTrigger>
        </TabsList>

        {/* ========== MIS COFRES ========== */}
        <TabsContent value="cofres">
          {/* Stats cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Card className="bg-[#121521] border border-white/[0.08]">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-[#F7F2E7]/50 mb-1">Cofres comprados</p>
                <p className="text-2xl font-heading font-bold text-[#FFC640] tabular-nums">{purchases?.total_chests || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#121521] border border-white/[0.08]">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-[#F7F2E7]/50 mb-1">Total invertido</p>
                <p className="text-2xl font-heading font-bold text-[#FFC640] tabular-nums">${purchases?.total_spent?.toFixed(2) || '0.00'}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#121521] border border-white/[0.08]">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-[#F7F2E7]/50 mb-1">Compras</p>
                <p className="text-2xl font-heading font-bold text-[#FFC640] tabular-nums">{purchases?.total_purchases || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#121521] border border-white/[0.08]">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-[#F7F2E7]/50 mb-1">Red total</p>
                <p className="text-2xl font-heading font-bold text-[#FFC640] tabular-nums">{network?.total_network || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Purchase history */}
          {purchases?.purchases?.length === 0 ? (
            <Card className="bg-[#121521] border border-white/[0.08]">
              <CardContent className="p-8 text-center">
                <Ticket size={32} className="text-[#F7F2E7]/20 mx-auto mb-3" />
                <p className="text-[#F7F2E7]/50">Aún no tienes cofres comprados</p>
                <p className="text-xs text-[#F7F2E7]/30 mt-1">Ve al tablero de cofres y abre tu primer cofre</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {purchases?.purchases?.map((purchase, idx) => (
                <Card key={purchase.purchase_id || idx} className="bg-[#121521] border border-white/[0.08]">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`px-2.5 py-1 rounded-lg text-xs font-heading font-bold ${
                          purchase.mode === 'diamante'
                            ? 'bg-[rgba(75,163,255,0.1)] text-[#4BA3FF] border border-[rgba(75,163,255,0.2)]'
                            : 'bg-[rgba(255,198,64,0.1)] text-[#FFC640] border border-[rgba(255,198,64,0.2)]'
                        }`}>
                          {purchase.mode === 'diamante' ? (
                            <><Star size={12} className="inline mr-1" />DIAMANTE</>
                          ) : (
                            <><Package size={12} className="inline mr-1" />BÁSICO</>
                          )}
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                          purchase.status === 'paid' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
                          purchase.status === 'pending_payment' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                          'bg-red-500/10 text-red-400 border border-red-500/20'
                        }`}>
                          {purchase.status === 'paid' ? 'Pagado' : purchase.status === 'pending_payment' ? 'Pendiente' : 'Cancelado'}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="font-heading font-bold text-lg text-[#FFC640] tabular-nums">${purchase.total?.toFixed(2)}</p>
                        <p className="text-[10px] text-[#F7F2E7]/40">
                          {purchase.created_at ? new Date(purchase.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        </p>
                      </div>
                    </div>

                    {/* Chest details grid */}
                    <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
                      {(purchase.chest_details || []).map((chest) => (
                        <div key={chest.chest_number}
                          className={`p-2 rounded-lg text-center ${
                            chest.is_free || chest.price === 0
                              ? 'bg-[rgba(53,208,127,0.08)] border border-[rgba(53,208,127,0.2)]'
                              : 'bg-[rgba(255,198,64,0.06)] border border-[rgba(255,198,64,0.15)]'
                          }`}
                        >
                          <p className="text-xs font-heading font-semibold text-[#F7F2E7] tabular-nums">
                            {chest.chest_label}
                          </p>
                          <p className={`text-[10px] font-heading font-bold mt-0.5 tabular-nums ${
                            chest.is_free || chest.price === 0 ? 'text-[#35D07F]' : 'text-[#FFC640]'
                          }`}>
                            {chest.is_free || chest.price === 0 ? 'GRATIS' : `$${chest.price?.toFixed(2)}`}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ========== MI RED ========== */}
        <TabsContent value="red">
          {/* Mode info banner */}
          {selectedRaffle?.template_type === 'free' ? (
            <div className="mb-6 p-4 bg-[#121521] rounded-xl border border-[rgba(53,208,127,0.2)]">
              <p className="text-sm text-[#F7F2E7]/70">
                <span className="text-[#35D07F] font-medium">Sorteo Gratis</span> — Premios fijos por nivel. No hay modo Normal/Diamante.
                Si el ganador tiene referidos en su cadena, reciben los premios de la tabla GRATIS.
              </p>
            </div>
          ) : (
            <div className="mb-6 p-4 bg-[#121521] rounded-xl border border-[rgba(75,163,255,0.15)]">
              <p className="text-sm text-[#F7F2E7]/70">
                <span className="text-[#4BA3FF] font-medium">Tus posibles premios</span> dependen del modo con el que participe el ganador de tu red: <span className="text-[#FFC640] font-medium">BÁSICO</span> (1-2 cofres) o <span className="text-[#4BA3FF] font-medium">DIAMANTE</span> (3+ cofres).
              </p>
            </div>
          )}

          {/* Level summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-8">
            {[1, 2, 3, 4, 5].map((level) => (
              <Card key={level} className="bg-[#121521] border border-white/[0.08]">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-[#F7F2E7]/50 mb-1">Nivel {level}</p>
                  <p className="text-2xl font-heading font-bold text-[#FFC640] tabular-nums">
                    {network?.level_counts?.[String(level)] || 0}
                  </p>
                  {selectedRaffle?.template_type === 'free' ? (
                    <div className="mt-1">
                      <p className="text-[9px] text-[#35D07F]/70">Gratis: {PRIZE_MAP_GRATIS[level]}</p>
                    </div>
                  ) : (
                    <div className="mt-1 space-y-0.5">
                      <p className="text-[9px] text-[#FFC640]/50">Básico: {PRIZE_MAP_BASICO[level]}</p>
                      <p className="text-[9px] text-[#4BA3FF]/60">Diamante: {PRIZE_MAP_DIAMANTE[level]}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Total network */}
          <div className="flex items-center gap-4 mb-8 p-4 bg-[#121521] rounded-xl border border-white/[0.08]">
            <div className="w-12 h-12 rounded-xl bg-[rgba(255,198,64,0.1)] border border-[rgba(255,198,64,0.2)] flex items-center justify-center">
              <Users size={22} className="text-[#FFC640]" />
            </div>
            <div>
              <p className="text-sm text-[#F7F2E7]/60">Total en tu red</p>
              <p className="text-2xl font-heading font-bold text-[#F7F2E7]">{network?.total_network || 0} personas</p>
            </div>
          </div>

          {/* Per-level listing */}
          <Card className="bg-[#0E1016] border border-white/[0.08]">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading text-[#F7F2E7]">Detalle por Nivel</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={activeLevel} onValueChange={setActiveLevel}>
                <TabsList className="bg-[#121521] border border-white/[0.08] mb-4">
                  {[1, 2, 3, 4, 5].map((l) => (
                    <TabsTrigger key={l} value={String(l)} className="data-[state=active]:bg-[#FFC640] data-[state=active]:text-[#090A0D] text-[#F7F2E7]/60 text-xs">
                      Nivel {l}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {[1, 2, 3, 4, 5].map((level) => (
                  <TabsContent key={level} value={String(level)}>
                    <ScrollArea className="max-h-[400px]">
                      {(network?.levels?.[String(level)] || []).length === 0 ? (
                        <div className="text-center py-8">
                          <Users size={32} className="text-[#F7F2E7]/20 mx-auto mb-2" />
                          <p className="text-sm text-[#F7F2E7]/40">No hay referidos en este nivel</p>
                          <p className="text-xs text-[#F7F2E7]/30 mt-2">Comparte tu código para hacer crecer tu red</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {(network?.levels?.[String(level)] || []).map((u, i) => (
                            <div key={i} className="flex items-center justify-between p-3 bg-[#121521] rounded-xl border border-white/[0.06]">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-[rgba(255,198,64,0.1)] flex items-center justify-center">
                                  <UserCheck size={14} className="text-[#FFC640]" />
                                </div>
                                <div>
                                  <p className="text-sm text-[#F7F2E7]">{u.name}</p>
                                  <p className="text-xs text-[#F7F2E7]/40">{u.created_at ? new Date(u.created_at).toLocaleDateString('es-MX') : ''}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {u.has_purchases && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-green-500/10 text-green-400 border border-green-500/20">
                                    <ShoppingCart size={10} /> Con compras
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MiRed;
